import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, SlidersHorizontal } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { MachineCard } from "@/components/MachineCard";
import { EnquiryModal } from "@/components/EnquiryModal";
import { useSettings } from "@/context/SettingsContext";

const SECTION_LABEL = { used: "Used Machines", new: "New Machines", spare: "Spare Parts" };

export default function CategoryLanding({ category, listingType, basePath }) {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enquire, setEnquire] = useState(null);
  const sectionLabel = SECTION_LABEL[listingType] || "Machines";

  useEffect(() => {
    setLoading(true);
    api.get("/machines", { params: { listing_type: listingType, category_id: category.id, limit: 48, sort: "featured" } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category.id, listingType]);

  const onEnquire = useCallback((m) => setEnquire(m), []);

  return (
    <Layout>
      <Seo
        title={`${category.name} — ${sectionLabel} | ${settings.website_name || "Mashiniya"}`}
        description={`${category.description ? category.description + " " : ""}Browse ${total} ${sectionLabel.toLowerCase()} in ${category.name} on Mashiniya and connect directly with sellers.`}
        canonical={window.location.origin + basePath + "/" + category.slug}
      />
      {/* Category hero */}
      <div className="relative overflow-hidden border-b border-slate-200 bg-[hsl(var(--charcoal))]">
        {category.image_url && <img src={fileUrl(category.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--charcoal))] via-[hsl(var(--charcoal))]/85 to-[hsl(var(--charcoal))]/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <nav className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-300">
            <Link to="/" className="hover:text-orange">Home</Link>
            <span>/</span>
            <Link to={basePath} className="hover:text-orange">{sectionLabel}</Link>
            <span>/</span>
            <span className="text-white">{category.name}</span>
          </nav>
          <p className="text-xs font-bold uppercase tracking-widest text-orange">{sectionLabel} Category</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-white sm:text-4xl">{category.name}</h1>
          {category.description && <p className="mt-3 max-w-2xl text-slate-300">{category.description}</p>}
          <p className="mt-4 text-sm font-semibold text-white">{total} {listingType === "spare" ? "spare parts" : "machines"} available</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-slate-900">Available in {category.name}</h2>
          <Link to={`${basePath}?category=${category.id}`} data-testid="category-view-all" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <SlidersHorizontal size={15} /> All filters
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => <div key={i} className="h-80 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 py-20 text-center">
            <p className="text-slate-500">No {listingType === "spare" ? "spare parts" : "machines"} in this category yet.</p>
            <Link to={basePath} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[hsl(var(--brand))]">
              Browse all {sectionLabel} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
          </div>
        )}
      </div>

      <EnquiryModal machine={enquire} open={!!enquire} onClose={() => setEnquire(null)} />
    </Layout>
  );
}
